import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, onMounted } from 'vue';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import JustWatchTVPlayBtn from '@/pages/titleDetailV1/v1/hero/JustwatchTVPlayBtn.vue';
import TitleDetailInfoRow from '@/pages/titleDetailV1/components/titleDetailInfoRow.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { OfferLocation } from '@/helpers/offer-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getJWTVOffer } from '@/features/jwtv/config';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { useTitlePlayerModal } from '@/features/title-player/composables/useTitlePlayerModal';


export default /*#__PURE__*/_defineComponent({
  __name: 'AVODHeroSection',
  props: {
    title: null,
    additionalContexts: null
  },
  setup(__props: any) {

const props = __props as {
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
};



const languageStore = useLanguageStore();
const appStore = useAppStore();

const titleName = computed(() => {
	return props.title.__typename === 'Season' ? props.title.show.content.title : props.title.content.title;
});

const description = computed(() => {
	return props.title.content.shortDescription || '';
});

const imageUrls = computed(() => props.title.content.backdrops.map(backdrop => backdrop.backdropUrl));

const avodOffer = computed(
	() =>
		getJWTVOffer(props.title.offers, languageStore.country, 'AVOD') ||
		getJWTVOffer(props.title.offers, languageStore.country, 'SVOD')
);

// Offer for impression tracking
const offerForImpression = computed(() => avodOffer.value ?? null);

// Clickout URL for tracking
const clickoutUrl = computed(() => {
	if (!offerForImpression.value) return null;

	const objectType = props.title.__typename === 'Season' ? props.title.show.objectType : props.title.objectType;
	const objectId = props.title.__typename === 'Season' ? props.title.show.objectId : props.title.objectId;

	return new ClickoutUrl(offerForImpression.value)
		.setTitleSourceId(props.title?.id ?? null)
		.addContexts(new SnowplowTitleContextGraphql(objectId, objectType))
		.addContexts(props.additionalContexts)
		.setForTracking('offerLocation', OfferLocation.BACKDROP)
		.set('uct_country', languageStore.country)
		.updateContext('ClickoutContextGraphql', 'clickoutType', 'video_player')
		.updateContext('ClickoutContextGraphql', 'placement', 'backdrop')
		.updateContext('TitleContextGraphql', 'jwRawEntityId', props.title?.id ?? null)
		.setCanary(appStore.xServeTrack);
});

const { openPlayerModal } = useTitlePlayerModal(() => avodOffer.value?.package.shortName ?? '');

async function handlePlayClick() {
	if (!avodOffer.value || !clickoutUrl.value) return;
	await openPlayerModal({ offer: avodOffer.value, title: props.title, clickoutUrl: clickoutUrl.value });
}

// Read More Functionality
const isDescriptionExpanded = ref(false);
const showReadMoreButton = ref(false);
const descriptionRef = ref<HTMLElement | null>(null);

// Visibility tracking
const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

function onVisibilityChanged(isVisible: boolean) {
	if (!!clickoutUrl.value) onIntersection(clickoutUrl.value, isVisible);
}

const observeVisibilityOptions = computed(() => {
	return !!offerForImpression.value
		? {
				callback: onVisibilityChanged,
				...observableOptions,
		  }
		: null;
});

onMounted(() => {
	// Check if description exceeds 3 lines
	if (descriptionRef.value) {
		const isClamped = descriptionRef.value.scrollHeight > descriptionRef.value.clientHeight;
		showReadMoreButton.value = isClamped;
	}
});

function toggleDescription() {
	isDescriptionExpanded.value = !isDescriptionExpanded.value;
	TrackingHelper.trackEvent('userinteraction', {
		action: isDescriptionExpanded.value ? 'read_more_clicked' : 'read_less_clicked',
		label: 'synopsis',
	});
}

return { __sfc: true,props, languageStore, appStore, titleName, description, imageUrls, avodOffer, offerForImpression, clickoutUrl, openPlayerModal, handlePlayClick, isDescriptionExpanded, showReadMoreButton, descriptionRef, onIntersection, onVisibilityChanged, observeVisibilityOptions, toggleDescription, JustWatchTVPlayBtn, TitleDetailInfoRow, PictureComp }
}

})